import {Button, Stack, Typography} from '@mui/material';
import React, {Fragment} from 'react';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {BeneficiaryCard, CTAButtonFixed, MainFooter, StyledGrid} from 'presentation/components';
import {ContactCard} from 'presentation/components/ContactCard';
import {Loading} from 'presentation/components/Loading/Loading';
import {usePersonalData} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {InfoBox} from 'presentation/pages/private/refund/components/InfoBox';
import {useUploadedRefundFilesStore} from 'presentation/pages/private/refund/components/UploadRefundFiles/store/useUploadedRefundFilesStore';
import {translations} from 'presentation/translations-new';
import {truncateText} from 'presentation/utils';
import {useSendAdditionalDocsDataStore} from '../../store/useSendAdditionalDocsDataStore';
import {useStepSendDocsStore} from '../../store/useStepSendDocsStore';
import {FormStepEnum} from '../../types';
import {CustomContainer, StyledBox, StyledFooterContainer} from './styles';
import {ConferenceProps} from './types';

const {
	finishRequest,
	attachment,
	requestCard: {description},
} = translations['pt-br'].privateFlow.refund.conferencePage;
const {title} = translations['pt-br'].privateFlow.refund.additionalDocuments.refundInfo;
const {change, requestLabel, beneficiaryData} = translations['pt-br'].commons;

export function Conference({handleNextStep, isLoading}: ConferenceProps) {
	const {isMobile, isLaptop, isTablet} = useMediaQueries();
	const uploadedFiles = useUploadedRefundFilesStore(state => state.uploadedFiles);
	const {currentEmail, currentPhone} = usePersonalData();
	const sendAdditionalDocsData = useSendAdditionalDocsDataStore(state => state.data);
	const setFormStep = useStepSendDocsStore(state => state.setStep);

	return (
		<Fragment>
			<CustomContainer>
				<StyledGrid.Root spacing={3}>
					<StyledGrid.Item md={12} lg={8}>
						<Typography variant="body2" color="text.secondary" fontWeight={600} mb={2}>
							{requestLabel}
						</Typography>
						<StyledBox>
							<InfoBox
								title={title}
								value={sendAdditionalDocsData.request}
								showButton={false}
							/>

							<InfoBox
								title={description}
								value={sendAdditionalDocsData.observations}
								onButtonClick={() => setFormStep(FormStepEnum.SEARCH_PROTOCOL)}
							/>

							{uploadedFiles?.length > 0 ? (
								<Stack gap={1}>
									<Stack
										justifyContent="space-between"
										alignItems="center"
										flexDirection="row">
										<Typography
											variant="body2"
											color="text.secondary"
											fontWeight={600}>
											{attachment}
										</Typography>
										<Button
											variant="text"
											size="medium"
											onClick={() => setFormStep(FormStepEnum.SENDING_FILES)}>
											{change}
										</Button>
									</Stack>
									{uploadedFiles.map(file => (
										<Stack
											key={file.id}
											flexDirection="row"
											alignContent="center"
											gap={1}
											flexWrap="wrap-reverse">
											<Icon name="upload-file-filled" />
											<Typography
												variant="subtitle1"
												color="text.primary"
												key={file.id}>
												{isMobile
													? truncateText({
															text: file.filename,
															containerWidth: 260,
															targetWidth: 300,
															txtWidth: 260,
															shrinkFactor: 2,
														})
													: file.filename}
											</Typography>
										</Stack>
									))}
								</Stack>
							) : null}
						</StyledBox>

						{!isLaptop && !isTablet ? (
							<CTAButtonFixed.Root justifyContent="space-between" mt={2}>
								<Stack
									flex={1}
									gap={1}
									justifyContent="flex-end"
									flexDirection={isMobile ? 'column' : 'row'}>
									<CTAButtonFixed.Action
										data-tag-id={
											RefundTaggingEnum.CLICK_FINALIZAR_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
										}
										disabled={isLoading}
										size="large"
										onClick={handleNextStep}
										variant="contained"
										fullWidth={isMobile}
										endIcon={
											isLoading ? <Loading size={20} color="inherit" /> : null
										}>
										{finishRequest}
									</CTAButtonFixed.Action>
								</Stack>
							</CTAButtonFixed.Root>
						) : null}
					</StyledGrid.Item>

					<StyledGrid.Item md={12} lg={4}>
						<Typography variant="body2" color="text.secondary" fontWeight={600} mb={2}>
							{beneficiaryData}
						</Typography>
						<Stack spacing={2}>
							<BeneficiaryCard shouldShowAnsPlanRegister={false} />
							<ContactCard user={{email: currentEmail, cellphone: currentPhone}} />
						</Stack>
					</StyledGrid.Item>
				</StyledGrid.Root>

				{isLaptop || isTablet ? (
					<CTAButtonFixed.Root justifyContent="space-between" mt={2}>
						<Stack flex={1} gap={1} justifyContent="flex-end" flexDirection={'row'}>
							<CTAButtonFixed.Action
								data-tag-id={
									RefundTaggingEnum.CLICK_FINALIZAR_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
								}
								disabled={isLoading}
								size="large"
								onClick={handleNextStep}
								variant="contained"
								fullWidth={isMobile}
								endIcon={isLoading ? <Loading size={20} color="inherit" /> : null}>
								{finishRequest}
							</CTAButtonFixed.Action>
						</Stack>
					</CTAButtonFixed.Root>
				) : null}
			</CustomContainer>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
